import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Rule } from "@aptsi-types";
import { environment } from "@env";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ErrorHandlingService } from "../shared/errorHandling/errorHandling.service";
import { API_ENDPOINTS } from "../ada-access-api-endpoints";

interface GetRulesResponse {
  rules: Rule[];
}

interface GetRuleResponse {
  rule: Rule;
}

@Injectable({
  providedIn: "root",
})
export class RuleService {
  private readonly ruleUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) { }

  getRiskAttributes(): Observable<{ riskAttributes: string[] }> {
    return this.http
      .post<{ riskAttributes: string[] }>(
        `${this.ruleUrl}${API_ENDPOINTS.RULE.GETRISKATTRIBUTE}`,
        {}
      ) // Ensure the URL is correct
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getRules(
    currentPage: number,
    sessionId: string,
    userId: string
  ): Observable<Rule[]> {
    const headers = {
      sessionId: sessionId,
      userId: userId,
    };

    return this.http
      .post<GetRulesResponse>(
        `${this.ruleUrl}${API_ENDPOINTS.RULE.FETCH}`,
        {
          currentPage,
        },
        {
          headers: headers, // Add the custom headers
        }
      )
      .pipe(
        map((response) => response.rules),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getRuleSearch(
    currentPage: number,
    searchText: String,
    filterByHeader: String
  ): Observable<Rule[]> {
    return this.http
      .post<GetRulesResponse>(`${this.ruleUrl}${API_ENDPOINTS.RULE.SEARCH}`, {
        currentPage,
        searchText,
        filterByHeader,
      })
      .pipe(
        map((response) => response.rules),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getAllRules(nullver: number): Observable<Rule[]> {
    return this.http
      .post<GetRulesResponse>(`${this.ruleUrl}${API_ENDPOINTS.RULE.GETALL}`, {
        nullver,
      })
      .pipe(
        map((response) => response.rules),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getRule(ruleid: string): Observable<Rule> {
    return this.http
      .post<GetRuleResponse>(`${this.ruleUrl}${API_ENDPOINTS.RULE.GET}`, {
        ruleid,
      })
      .pipe(
        map((response) => response.rule),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  createRule(
    name: string,
    type: string,
    representation: string,
    organizationId: string
  ): Observable<Rule> {
    return this.http
      .post<Rule>(`${this.ruleUrl}${API_ENDPOINTS.RULE.CREATE}`, {
        name,
        type,
        representation,
        organizationId
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  updateRule(
    ruleid: string,
    name: string,
    type: string,
    representation: string,
    organizationId: string
  ): Observable<Rule> {
    return this.http
      .post<Rule>(`${this.ruleUrl}${API_ENDPOINTS.RULE.UPDATE}`, {
        ruleid,
        name,
        type,
        representation,
        organizationId
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  deleteRule(ruleid: string): Observable<Rule> {
    return this.http
      .post<Rule>(`${this.ruleUrl}${API_ENDPOINTS.RULE.DELETE}`, { ruleid })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }
}
